/**
 * DeltaChip - small mono change chip ("Data Product" v3).
 * Green wash = up, red wash = down, amber wash = caution (used for the
 * naira: a rising NGN/USD rate means the naira weakened - never a win).
 *
 * Two cases the obvious version gets wrong, both of which put the colour and
 * the number in contradiction:
 *
 * A move too small to show at this precision. The direction came from the raw
 * value while the number was rounded separately, so NGN/USD slipping 60 kobo
 * on 1,347 (-0.0445%) rendered as a red "▼ −0.0%": an arrow saying it fell
 * beside a figure saying it did not move. Such a move now reads "▼ <0.1%",
 * which is both true and obviously not zero.
 *
 * No move at all. `v >= 0` counted flat as up, so an unchanged rate got the
 * green wash and an up arrow, which reads as a gain. Zero is now neutral and
 * carries no arrow, because nothing happened.
 */

import React from 'react';
import { Box } from '@mui/material';
import { useThemeMode, numericSx } from '../../context/ThemeContext';

function DeltaChip({ value, caution = false, suffix = '', decimals = 1, size = 'md' }) {
  const { palette } = useThemeMode();
  const v = Number(value) || 0;
  const up = v > 0;
  const flat = v === 0;

  // What this precision can actually show. Below half of it the figure
  // rounds away to zero and would contradict the arrow beside it.
  const smallest = 10 ** -decimals;
  const roundsToZero = !flat && Number(Math.abs(v).toFixed(decimals)) === 0;

  const color = flat
    ? palette.textSecondary
    : caution
      ? palette.warning
      : up ? palette.accent : palette.danger;
  const bg = flat
    ? palette.surface
    : caution
      ? palette.warningBg
      : up ? palette.accentBg : palette.dangerBg;
  const small = size === 'sm';

  const arrow = flat ? '' : up ? '▲' : '▼';
  const sign = flat ? '' : up ? '+' : '−';
  const figure = roundsToZero
    ? `<${smallest.toFixed(decimals)}`
    : Math.abs(v).toFixed(decimals);

  return (
    <Box
      component="span"
      sx={{
        ...numericSx,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: small ? 0.7 : 0.9,
        py: small ? 0.2 : 0.3,
        borderRadius: '6px',
        fontSize: small ? '0.68rem' : '0.76rem',
        fontWeight: 500,
        lineHeight: 1.4,
        color,
        bgcolor: bg,
        whiteSpace: 'nowrap',
      }}
    >
      {/* A "<" already carries the sign's meaning, so pairing them would
          read as "minus less than", which is not what is meant. */}
      {arrow}{arrow ? ' ' : ''}{roundsToZero ? '' : sign}{figure}%{suffix ? ` ${suffix}` : ''}
    </Box>
  );
}

export default DeltaChip;
